import './view-result.scss';

import { useEffect, useMemo, useState } from 'react';

import { toResultType } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { CopyValue } from '../../utils/copy';
import TabMessage from '../child-agent/tabs/TabMessage';
import { DocIcon, EntityContextHeader, VsrTabs, WarnGlyph } from './components';
import { defaultResultState, formatINECDate, type IElectoralDivisionCode } from './helpers';
import ResultDataTab from './ResultDataTab';
import ResultSheetTab from './ResultSheetTab';
import SituationTab from './SituationTab';
import { toRefFetchableLevel, useGetResultsByRefID } from './use-get-result-by-ref-id';
import { buildLocationContext, buildSitrepContext, hasProjectedResult, levelLabel, VSR_TABS, type VsrTab } from './utils';

// `httpGetMethod` sends `timeout: null` and `apiRequest` resolves that to `timeout || 0`, which
// axios reads as NO timeout. A dropped socket therefore leaves the promise pending and neither
// callback ever fires. This watchdog is the only thing that makes "the spinner is always bounded"
// true rather than aspirational.
const LOAD_TIMEOUT_MS = 20000;

type LoadPhase =
	{ status: 'idle' } | { status: 'loading' } | { status: 'loaded' } | { status: 'error'; kind: 'failed' | 'timeout' };

const ViewSubmitedResult = () => {
	const { rsProps: rightPanel } = useGlobalContext();

	const { referenceId, electoralDivision } = (rightPanel?.data ?? {}) as {
		electionName: string;
		electoralDivision: { name: string; value: IElectoralDivisionCode };
		referenceId: string;
		id: string;
	};

	// The level arrives lower case from the API and every comparison below is upper case, so the
	// panel normalises its own copy too — the hook cannot reach these reads.
	const rawResultType = rightPanel?.resultType;
	const resultType = toResultType(rawResultType);

	// "Fetchable" is narrower than "valid": EC8E and the EC40* forms are real ResultTypes with no
	// by-reference endpoint. Treating them as fetchable makes the panel blame the network for a
	// request it was never going to make, and offer a retry that can never succeed.
	const fetchableLevel = toRefFetchableLevel(rawResultType);

	// Nothing to fetch without both halves of the key. Say so rather than requesting `/undefined`.
	const canFetch = !!referenceId && !!fetchableLevel;

	const [phase, setPhase] = useState<LoadPhase>({ status: 'idle' });
	const [tab, setTab] = useState<VsrTab>('sheet');

	const resultByIdRefIdProps = useGetResultsByRefID({
		rstype: resultType,
		onSuccess: () => setPhase({ status: 'loaded' }),
		onFailure: () => setPhase({ status: 'error', kind: 'failed' }),
	});

	const load = () => {
		if (!canFetch) {
			setPhase({ status: 'idle' });
			return;
		}
		setPhase({ status: 'loading' });
		resultByIdRefIdProps.getResults(referenceId);
	};

	useEffect(() => {
		setTab('sheet');
		load();
	}, [referenceId, resultType]);

	// Depend on `phase`, not `phase.status`: back-to-back loads produce a fresh object, so the old
	// timer is cleared. Keying on the string would leave a stale timer armed.
	useEffect(() => {
		if (phase.status !== 'loading') return;
		const timer = window.setTimeout(() => setPhase({ status: 'error', kind: 'timeout' }), LOAD_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [phase]);

	const result = useMemo(() => resultByIdRefIdProps.response || defaultResultState, [resultByIdRefIdProps.response]);

	// Derive from the store value, not from which callback fired: the store value is exactly what
	// the tabs render, so the message always matches what the agent would otherwise be shown.
	const view = useMemo(() => {
		if (!canFetch) return 'unavailable' as const;
		if (phase.status === 'idle' || phase.status === 'loading') return 'loading' as const;
		if (phase.status === 'error') return 'error' as const;
		return hasProjectedResult(result) ? ('ready' as const) : ('pending' as const);
	}, [canFetch, phase, result]);

	const sitrepCtx = useMemo(() => buildSitrepContext(result), [result]);

	const contextFields = useMemo(() => {
		const isWardUp = resultType === 'EC8C' || resultType === 'EC8D';
		const isLgaUp = resultType === 'EC8D';
		return [
			{
				label: result?.approvedBy?.name ? 'Approved' : 'Date',
				value: result?.approvedAt ? formatINECDate(result.approvedAt) : '—',
			},
			{ label: 'State', value: result?.state?.name || '' },
			{ label: isLgaUp ? '' : 'LGA', value: result?.localGovernment?.name || '' },
			{ label: isLgaUp ? '' : 'LGA Code', value: result?.localGovernment?.code || '', mono: true, copy: true },
			{ label: isWardUp ? '' : 'RA/Ward', value: result?.ward?.name || '' },
			{ label: isWardUp ? '' : 'RA/Ward Code', value: result?.ward?.code || '', mono: true, copy: true },
			{ label: resultType === 'EC8A' ? 'Polling Unit' : '', value: result?.pollingUnit?.name || '' },
			{ label: resultType === 'EC8A' ? 'Polling Unit Code' : '', value: result?.pollingUnit?.code || '', mono: true, copy: true },
			{ label: 'Presiding Officer', value: result?.presidingOfficer?.name || '—' },
		];
	}, [result, resultType]);

	const onCreateSituation = () => {
		rightPanel?.addRightSectionHistory();
		rightPanel?.callSection({
			action: 'create',
			component: 'reports-hub',
			title: 'Reports',
			max: true,
			data: {
				initialSegment: 'new',
				sitrepLocation: buildLocationContext(result, resultType),
			},
		});
	};

	if (view === 'loading') {
		return (
			<div className='ca-result__skeleton f-column-15'>
				<div className='ca-result__sk-image skeleton' />
				<div className='ca-result__sk-grid'>
					<div className='ca-result__sk-cell skeleton' />
					<div className='ca-result__sk-cell skeleton' />
					<div className='ca-result__sk-cell skeleton' />
					<div className='ca-result__sk-cell skeleton' />
				</div>
				<div className='ca-result__sk-bar skeleton' />
				<div className='ca-result__sk-bar skeleton' />
			</div>
		);
	}

	if (view === 'unavailable') {
		// Two distinct dead ends, and naming which one saves a support round-trip.
		return !fetchableLevel && rawResultType ? (
			<TabMessage
				icon={<DocIcon />}
				title='This result level is not supported here'
				hint={`Your assignment is recorded as “${rawResultType}”. This view opens polling unit, ward, LGA and state results. Tell your party coordinator if that is wrong.`}
			/>
		) : (
			<TabMessage
				icon={<DocIcon />}
				title='No result to show yet'
				hint='Submit your assigned result and it will appear here.'
			/>
		);
	}

	if (view === 'error') {
		const timedOut = phase.status === 'error' && phase.kind === 'timeout';
		return (
			<TabMessage
				icon={<WarnGlyph />}
				tone='danger'
				title={timedOut ? 'This is taking longer than usual' : 'We could not load this result'}
				hint={
					timedOut
						? 'The result did not arrive in 20 seconds. Check your connection, then try again.'
						: 'The result service did not respond. Check your connection, then try again.'
				}
				action={{ label: 'Try again', onClick: load }}
			/>
		);
	}

	if (view === 'pending') {
		// A 200 with an empty body — the command→query projection has not caught up. The agent still
		// leaves with the reference, which is the thing that proves the submission happened.
		return (
			<div className='f-column-20'>
				<TabMessage
					icon={<DocIcon />}
					title='Your result is still being saved'
					hint={`We received your submission${electoralDivision?.name ? ` for ${electoralDivision.name}` : ''}. The figures usually appear within a few minutes.`}
					action={{ label: 'Check again', onClick: load }}
				/>
				<div className='f-column-4 align-items-center'>
					<span className='vsr-context__cell-label'>Reference</span>
					<span className='vsr-context__cell-value is-mono'>
						<CopyValue
							value={referenceId}
							label='reference'
							variant='inline'
						/>
					</span>
				</div>
			</div>
		);
	}

	const headerTitle =
		result?.pollingUnit?.name || result?.ward?.name || result?.localGovernment?.name || result?.state?.name || 'Submitted result';

	return (
		<div className='f-column-20'>
			<EntityContextHeader
				eyebrow={`${levelLabel(resultType)} result`}
				title={headerTitle}
				status={result?.approvedBy?.name ? result?.status : null}
				fields={contextFields}
			/>

			<VsrTabs
				tabs={VSR_TABS}
				active={tab}
				onChange={setTab}
			/>

			<div
				className='vsr-panel'
				role='tabpanel'
				id={`vsr-panel-${tab}`}
				aria-labelledby={`vsr-tab-${tab}`}
			>
				{tab === 'sheet' ? (
					<ResultSheetTab
						result={result}
						referenceId={referenceId}
						withVideo={resultType === 'EC8A'}
					/>
				) : null}
				{tab === 'data' ? <ResultDataTab result={result} /> : null}
				{tab === 'sitrep' ? (
					<SituationTab
						sitrepCtx={sitrepCtx}
						onCreate={onCreateSituation}
					/>
				) : null}
			</div>
		</div>
	);
};

export default ViewSubmitedResult;
